import React, { useEffect } from 'react';
import {
    IonPage,
    IonGrid,
    IonRow
} from '@ionic/react';
import { Redirect, Route } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { AppHeader } from './AppHeader';
import { CaremapEnqueueErrorToaster } from './caremap/CaremapEnqueueErrorToaster';
import { CareMapDrawerState } from './caremap/CareMapDrawerState';
import { DebugPage } from './support/DebugPage';
import { LiveUpdatesListener } from '@meraki-internal/live-updates';
import { IntercomService } from './support/IntercomService';
import { HistoryViewModel } from './support/HistoryViewModel';
import { NavigationMenu } from './navigation/NavigationMenu';
import { FeatureFlags } from './support/feature-flags/FeatureFlags';
import { InvitationAcceptancePage } from './share-caremap/InvitationAcceptancePage';
import { InnerhivePlusDialog } from './innerhive-plus/InnerhivePlusDialog';
import { ConnectCalendarModal } from './appointments/ConnectCalendarModal';
import { DefaultRouter } from './routers/DefaultRouter';
import { BlockUntilActiveCaremap } from './ActiveCareMapProvider';
import { HouseholdRouter } from './routers/HouseholdRouter';
import { CareMapRouter } from './routers/CareMapRouter';
import { SupportGroupRouter } from './routers/SupportGroupRouter';
import { OrganizationsRouter } from './routers/OrganizationsRouter';
import { ActiveCaremapRouter } from './routers/ActiveCaremapRouter';
import { NotificationConsentDialog } from './push-notifications/NotificationsConsentDialog';
import { CaregiverRouter } from './routers/CaregiverRouter';
import { OrganizationsState } from './organizations/OrganizationsState';
import { CareRouter } from './routers/CareRouter';
import { QueryParamDrivenDrawer } from './components/query-param-driver-drawer/QueryParamDrivenDrawer';
import { MiniWizardRouteHandler } from './wizard/mini-wizard/MiniWizardPage';
import { CommunityRouter } from './routers/CommunityRouter';
import { AnonymousPromoter } from './auth/anonymous/AnonymousPromoter';
import { NotesPageRouteHandler } from './notes/CareMapNotesPage';
import { FailedAnonymousSessionAlerter } from './auth/anonymous/FailedAnonymousSessionAlerter';
import { IntercomUXAdapter } from './support/intercom/IntercomUXAdapter';
import { ToastBinder } from './ToastBinder';
import { WebSocketListener } from './web-sockets/WebSocketListener';
import { TabNavigation } from './navigation/TabNavigation';
import { EditCheckinModal } from './journal/details/EditCheckinModal';
import { CalendarRouter } from './routers/CalendarRouter';
import { HelperBeeModal } from './helper-bee/HelperBeeModal';
import { InAppNotificationBinder } from './push-notifications/InAppNotificationBinder';
import { GlobalActionHandler } from './GlobalActionHandler';
import { GlobalRecordingModals } from './GlobalRecordingModals';
import { PaywallTriggerHandler } from './innerhive-plus/PaywallTriggerHandler';

const useStyles = createUseStyles({
    grid: {
        padding: 0,
        width: '100%',

        '--ion-grid-column-padding-xs': 0,
        '--ion-grid-column-padding-sm': 0,

        '& > ion-row': {
            height: '100%'
        }
    },
});

/**
 * Not actually a state class, as it doesn't need to support subscription (yet).
 * Provides a way to track a boolean indicating when the app is done loading.
 * This can be observced by listeners in other components that need to behave
 * differently if they fire during app start (e.g. PushNotificationListener).
 */
export class AppState {
    private loaded: boolean = false;
    setLoaded = () => {
        this.loaded = true;
    };
    isLoaded = () => {
        return this.loaded;
    };
}

const AppLoaded: React.FC = () => {
    const appState = useInstance(AppState);
    useEffect(() => {
        appState.setLoaded();
    });
    return null;
};

export const InnerhiveApp: React.FC = () => {
    const classes = useStyles();

    const organizations = useInstance(OrganizationsState);
    useSubscription(() => organizations);

    const drawerState = useInstance(CareMapDrawerState);
    useSubscription(() => drawerState);

    const intercomService = useInstance(IntercomService);
    const historyViewModel = useInstance(HistoryViewModel);

    const isInvite = historyViewModel.getCurrentLocation().pathname === '/accept-invite';

    let pageTitle: React.ReactNode | string = undefined;
    if (isInvite) {
        pageTitle = <img height="24px" src="/assets/images/logos/logo-with-text-white.svg" alt="logo" />;
    }

    return (

        <IonPage>
            <IntercomUXAdapter />
            <LiveUpdatesListener />
            <AppHeader title={pageTitle} />
            <CaremapEnqueueErrorToaster />

            <InnerhivePlusDialog />
            <NotificationConsentDialog />
            <ConnectCalendarModal />
            <DebugPage />
            <EditCheckinModal />
            <HelperBeeModal />
            <GlobalRecordingModals />

            <QueryParamDrivenDrawer />
            <AnonymousPromoter />
            <ToastBinder />
            <PaywallTriggerHandler />
            <InAppNotificationBinder />
            <FailedAnonymousSessionAlerter />
            <WebSocketListener />

            <Route exact path="/accept-invite" render={(routeProps) => {
                const params = new URLSearchParams(routeProps.location.search);
                return (
                    <InvitationAcceptancePage invite={params.get('invite') || undefined} />
                );
            }} />

            <CaregiverRouter />

            <OrganizationsRouter />

            <CareRouter />

            <CalendarRouter />

            <CommunityRouter />

            <NotesPageRouteHandler />

            <MiniWizardRouteHandler />

            <Route path={['/caremaps', '/caremap', '/questionnaire', '/tour', '/add-caremap', '/active-caremap']}>
                <IonGrid className={classes.grid} id="innerhive-grid">
                    <IonRow>
                        <Route exact path="/tour/:id" render={(routeProps) => {
                            const id: number = parseInt(routeProps.match.params.id);
                            const params = new URLSearchParams(routeProps.location.search);
                            const path: string = params.get('path') || 'caremaps';
                            intercomService.startTour(id);
                            historyViewModel.replace(`/#/${path}`);
                            return undefined;
                        }} />

                        <CareMapRouter />

                        {/* careMapId is optional, in order to catch old / bookmarked urls */}
                        <Route path="/questionnaire/:careMapId?" render={(props) => {
                            if (props.match.params.careMapId){
                                return <Redirect to={`/caregiver?id=${props.match.params.careMapId}`} />;
                            }
                            return <Redirect to="/" />;

                        }}>
                        </Route>
                    </IonRow>
                </IonGrid>
                <BlockUntilActiveCaremap>
                    <HouseholdRouter />
                    <SupportGroupRouter />
                    <NavigationMenu contentId="innerhive-grid" />
                </BlockUntilActiveCaremap>

                <ActiveCaremapRouter />
            </Route>

            <Route exact path="/feature-flags">
                <FeatureFlags />
            </Route>

            <Route exact path="/">
                <DefaultRouter />
            </Route>

            <TabNavigation />

            <AppLoaded />

            <GlobalActionHandler />
        </IonPage>
    );
};
